import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu';
import {
  Search,
  SortAsc,
  SortDesc,
  Filter,
  X,
  Calendar,
  Building2,
  DollarSign,
  Users,
  AlertCircle,
  MoreHorizontal,
  Eye,
  Download,
  RefreshCw
} from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SearchSortCriteria {
  search: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  itemsPerPage: number;
  currentPage: number;
}

export interface SearchField {
  key: string;
  label: string;
  placeholder: string;
  type: 'text' | 'number' | 'date';
}

export interface SortOption {
  key: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
}

interface SearchAndSortProps {
  criteria: SearchSortCriteria;
  onCriteriaChange: (criteria: SearchSortCriteria) => void;
  searchFields?: SearchField[];
  sortOptions?: SortOption[];
  totalItems?: number;
  isLoading?: boolean;
  className?: string;
  showItemsPerPage?: boolean;
  showResultCount?: boolean;
  showBulkActions?: boolean;
  selectedItems?: string[];
  onSelectedItemsChange?: (items: string[]) => void;
  onRefresh?: () => void;
  onExport?: () => void;
  onBulkAction?: (action: string, items: string[]) => void;
}

const defaultSearchFields: SearchField[] = [
  { key: 'title', label: 'Title', placeholder: 'Search by title...', type: 'text' },
  { key: 'organization', label: 'Organization', placeholder: 'Search by organization...', type: 'text' },
  { key: 'vendor', label: 'Vendor', placeholder: 'Search by vendor...', type: 'text' },
];

const defaultSortOptions: SortOption[] = [
  { key: 'createdAt', label: 'Created Date', icon: Calendar },
  { key: 'updatedAt', label: 'Last Updated', icon: Calendar },
  { key: 'title', label: 'Title', icon: Building2 },
  { key: 'organizationName', label: 'Organization', icon: Building2 },
  { key: 'estimatedBudget', label: 'Budget', icon: DollarSign },
  { key: 'participantCount', label: 'Participants', icon: Users },
  { key: 'priority', label: 'Priority', icon: AlertCircle },
];

const itemsPerPageOptions = [10, 25, 50, 100];

export default function SearchAndSort({
  criteria,
  onCriteriaChange,
  searchFields = defaultSearchFields,
  sortOptions = defaultSortOptions,
  totalItems = 0,
  isLoading = false,
  className,
  showItemsPerPage = true,
  showResultCount = true,
  showBulkActions = false,
  selectedItems = [],
  onSelectedItemsChange,
  onRefresh,
  onExport,
  onBulkAction
}: SearchAndSortProps) {
  const [activeSearchField, setActiveSearchField] = useState(searchFields[0]?.key || 'title');
  const [searchQuery, setSearchQuery] = useState(criteria.search);

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      onCriteriaChange({
        ...criteria,
        search: searchQuery,
        currentPage: 1 // Reset to first page on search
      });
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleSearch = (value: string) => {
    setSearchQuery(value);
  };

  const handleSort = (sortBy: string) => {
    const newOrder = criteria.sortBy === sortBy && criteria.sortOrder === 'asc' ? 'desc' : 'asc';
    onCriteriaChange({
      ...criteria,
      sortBy,
      sortOrder: newOrder
    });
  };

  const handleItemsPerPage = (itemsPerPage: number) => {
    onCriteriaChange({
      ...criteria,
      itemsPerPage,
      currentPage: 1 // Reset to first page when changing page size
    });
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  const clearAll = () => {
    setSearchQuery('');
    onCriteriaChange({
      search: '',
      sortBy: 'createdAt',
      sortOrder: 'desc',
      itemsPerPage: 25,
      currentPage: 1
    });
  };

  // Calculate pagination info
  const startItem = (criteria.currentPage - 1) * criteria.itemsPerPage + 1;
  const endItem = Math.min(criteria.currentPage * criteria.itemsPerPage, totalItems);
  const totalPages = Math.ceil(totalItems / criteria.itemsPerPage);

  const currentSortOption = sortOptions.find(option => option.key === criteria.sortBy);
  const selectedSearchField = searchFields.find(field => field.key === activeSearchField);

  return (
    <div className={cn('space-y-4', className)}>
      {/* Search Bar */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <div className="flex">
            {/* Search Field Selector */}
            <Select value={activeSearchField} onValueChange={setActiveSearchField}>
              <SelectTrigger className="w-auto rounded-r-none border-r-0 min-w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {searchFields.map((field) => (
                  <SelectItem key={field.key} value={field.key}>
                    {field.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder={selectedSearchField?.placeholder || 'Search...'}
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10 pr-10 rounded-l-none"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearSearch}
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {onRefresh && (
            <Button
              variant="outline"
              size="sm"
              onClick={onRefresh}
              disabled={isLoading}
            >
              <RefreshCw className={cn("w-4 h-4", isLoading && "animate-spin")} />
            </Button>
          )}

          {onExport && (
            <Button variant="outline" size="sm" onClick={onExport}>
              <Download className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Sort and View Options */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          {/* Sort Options */}
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  {criteria.sortOrder === 'asc' ? (
                    <SortAsc className="w-4 h-4 mr-2" />
                  ) : (
                    <SortDesc className="w-4 h-4 mr-2" />
                  )}
                  Sort by {currentSortOption?.label || 'Date'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuLabel>Sort Options</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {sortOptions.map((option) => {
                  const IconComponent = option.icon;
                  return (
                    <DropdownMenuItem
                      key={option.key}
                      onClick={() => handleSort(option.key)}
                      className={cn(
                        "flex items-center gap-2",
                        criteria.sortBy === option.key && "bg-muted"
                      )}
                    >
                      {IconComponent && <IconComponent className="w-4 h-4" />}
                      {option.label}
                      {criteria.sortBy === option.key && (
                        criteria.sortOrder === 'asc' ? (
                          <SortAsc className="w-4 h-4 ml-auto" />
                        ) : (
                          <SortDesc className="w-4 h-4 ml-auto" />
                        )
                      )}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Clear Filters */}
            {(criteria.search || criteria.sortBy !== 'createdAt' || criteria.sortOrder !== 'desc') && (
              <Button variant="ghost" size="sm" onClick={clearAll}>
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}
          </div>

          {/* Bulk Actions */}
          {showBulkActions && selectedItems.length > 0 && (
            <div className="flex items-center gap-2">
              <Badge variant="secondary">
                {selectedItems.length} selected
              </Badge>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <MoreHorizontal className="w-4 h-4" />
                    Actions
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => onBulkAction?.('export', selectedItems)}>
                    <Download className="w-4 h-4 mr-2" />
                    Export Selected
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onBulkAction?.('mark_read', selectedItems)}>
                    <Eye className="w-4 h-4 mr-2" />
                    Mark as Read
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onBulkAction?.('delete', selectedItems)}
                    className="text-destructive"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Delete Selected
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>

        <div className="flex items-center gap-4">
          {/* Items Per Page */}
          {showItemsPerPage && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Show:</span>
              <Select
                value={criteria.itemsPerPage.toString()}
                onValueChange={(value) => handleItemsPerPage(Number(value))}
              >
                <SelectTrigger className="w-auto">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {itemsPerPageOptions.map((option) => (
                    <SelectItem key={option} value={option.toString()}>
                      {option} items
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Result Count */}
          {showResultCount && (
            <div className="text-sm text-muted-foreground">
              {totalItems > 0 ? (
                <>
                  Showing {startItem}-{endItem} of {totalItems.toLocaleString()} results
                  {totalPages > 1 && ` (page ${criteria.currentPage} of ${totalPages})`}
                </>
              ) : (
                isLoading ? 'Loading...' : 'No results'
              )}
            </div>
          )}
        </div>
      </div>

      {/* Active Search Display */}
      {searchQuery && (
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-xs">
            <Search className="w-3 h-3 mr-1" />
            {selectedSearchField?.label}: "{searchQuery}"
            <Button
              variant="ghost"
              size="sm"
              className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
              onClick={clearSearch}
            >
              <X className="h-3 w-3" />
            </Button>
          </Badge>
        </div>
      )}
    </div>
  );
}

// Pagination Component
interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
  className?: string;
}

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  isLoading = false,
  className
}: PaginationProps) {
  const getVisiblePages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  if (totalPages <= 1) return null;

  const visiblePages = getVisiblePages();

  return (
    <div className={cn('flex items-center justify-center gap-1', className)}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage <= 1 || isLoading}
      >
        Previous
      </Button>

      {visiblePages.map((page, index) => (
        <React.Fragment key={index}>
          {page === '...' ? (
            <span className="px-2 text-muted-foreground">...</span>
          ) : (
            <Button
              variant={currentPage === page ? 'default' : 'outline'}
              size="sm"
              onClick={() => onPageChange(page as number)}
              disabled={isLoading}
              className="min-w-[40px]"
            >
              {page}
            </Button>
          )}
        </React.Fragment>
      ))}

      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage >= totalPages || isLoading}
      >
        Next
      </Button>
    </div>
  );
}